import React, { memo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/* ───────────── animation presets ───────────── */
const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: (i = 0) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.2, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
  hover: {
    y: -12, scale: 1.02, rotateX: 5,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

const bubbleFloat = {
  y: [0, -12, 0],
  x: [0, 3, -2, 0],
  rotate: [0, 0.8, -0.5, 0],
  transition: { duration: 7, ease: 'easeInOut' as const, repeat: Infinity, repeatType: 'reverse' as const },
};

/* ───────────── styled components ───────────── */
const StepCardWrapper = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 520px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.1);
  cursor: default;
  transform-style: preserve-3d;

  &:hover { box-shadow: 0 20px 60px rgba(0,0,0,0.15); }

  @media (max-width: 640px) { min-height: auto; }
`;

const SubscribeCardStyled = styled(StepCardWrapper)`
  background: linear-gradient(135deg, #ffdb00 0%, #ffb800 50%, #ff8a00 100%);
`;

/* carry the infinite float on a child layer to avoid fighting variants */
const FloatLayer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const CardTop = styled.div`
  padding: 1.75rem 2rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) { padding: 1.5rem 1.25rem; }
`;

const MonthlyClub = styled.div`
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  color: rgba(0,0,0,0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const Price = styled.div`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 2rem;
  color: #000;

  span { font-size: 1rem; opacity: 0.7; font-weight: 500; }

  @media (max-width: 640px) {
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
  }
`;

const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255,255,255,0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.4);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
`;

const FeatureText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(0,0,0,0.8);
  line-height: 1.3;

  @media (max-width: 640px) { font-size: 0.85rem; }
`;

const CardContent = styled.div`
  padding: 1.75rem 2rem 2rem;
  margin-top: auto;
  color: #000;

  @media (max-width: 640px) { padding: 1.5rem 1.25rem; }
`;

const StepTitle = styled.h3`
  font-size: 2rem;
  margin: 0 0 1rem;
  font-weight: 600;

  @media (max-width: 640px) { font-size: 1.5rem; }
`;

const StepDescription = styled.p`
  font-size: 1.125rem;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 640px) { font-size: 1rem; }
`;

/* ───────────── types & defaults ───────────── */
interface SubscribeCardProps {
  customIndex?: number;
  className?: string;
  price?: string;
  period?: string;
  features?: string[];
  hidePrice?: boolean;
}

const DEFAULT_FEATURES: string[] = [
  'Unlimited design & website requests',
  'Local SEO optimization',
  'Social media campaign assets',
  'Email banners & newsletter design',
  'Fast 48-hour average turnaround',
  'Monthly strategy insights & reporting',
  'Pause or cancel anytime',
  'Cheaper than an employee',
];

/* ───────────── helpers ───────────── */
function getFeatureIcon(feature: string) {
  const f = feature.toLowerCase();
  if (f.includes('request')) return '🎯';
  if (f.includes('seo')) return '📈';
  if (f.includes('social')) return '📱';
  if (f.includes('email')) return '✉️';
  if (f.includes('delivery') || f.includes('turnaround')) return '⚡';
  if (f.includes('strategy') || f.includes('insight')) return '🧠';
  if (f.includes('pause') || f.includes('cancel')) return '⏸️';
  if (f.includes('web')) return '💻';
  return '✓';
}

/* shallow value equality for arrays (order-sensitive) */
function arrayEqual(a?: string[], b?: string[]) {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/* ───────────── component ───────────── */
function SubscribeCardBase({
  customIndex = 0,
  className,
  price = '$1,495 - $5,995',
  period = '/month',
  features = DEFAULT_FEATURES,
  hidePrice = false,
}: SubscribeCardProps) {
  return (
    <SubscribeCardStyled
      className={className}
      custom={customIndex}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, amount: 0.2 }}
    >
      <FloatLayer animate={bubbleFloat}>
        <CardTop>
          <MonthlyClub>Monthly Club</MonthlyClub>

          {!hidePrice ? (
            <Price>
              {price}
              <span>{period}</span>
            </Price>
          ) : null}

          <FeaturesContainer>
            {features.map((feature, index) => (
              <FeatureCard
                key={`${feature}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <FeatureIcon>{getFeatureIcon(feature)}</FeatureIcon>
                <FeatureText>{feature}</FeatureText>
              </FeatureCard>
            ))}
          </FeaturesContainer>
        </CardTop>

        <CardContent>
          <StepTitle>Subscribe</StepTitle>
          <StepDescription>
            Subscribe for ongoing asset creation and design support. Perfect for start ups and small businesses that want growth without the overhead.
          </StepDescription>
        </CardContent>
      </FloatLayer>
    </SubscribeCardStyled>
  );
}

/* ───────────── memoization ───────────── */
function propsAreEqual(prev: Readonly<SubscribeCardProps>, next: Readonly<SubscribeCardProps>) {
  return (
    prev.customIndex === next.customIndex &&
    prev.className === next.className &&
    prev.price === next.price &&
    prev.period === next.period &&
    prev.hidePrice === next.hidePrice &&
    arrayEqual(prev.features, next.features)
  );
}

const SubscribeCard = memo(SubscribeCardBase, propsAreEqual);
SubscribeCard.displayName = 'SubscribeCard';

export default SubscribeCard;
